/**
 * Ask Zoye: a chat over the live agent configuration (Deivydas 2026-09-18).
 *
 * Ops kept asking him "how is the agent set up to handle refund saves for
 * LifeGauge?" — here they ask the app. The server hands the resolved config
 * (merged refund policy, playbooks, bot rules, prompt blocks) to the cheap
 * router model, instructed to cite its sources and to say "not configured"
 * rather than guess. "All brands" asks across every brand at once, and the
 * answer splits by brand wherever the config differs.
 *
 * The thread lives in this tab and dies with it; switching brand clears it,
 * because the answers were about the other brand's config. The QUESTIONS are
 * logged — who, when, which brand — in the panel on the right, which is also
 * how what ops actually needs to know becomes visible.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { CornerDownLeft, Eraser } from 'lucide-react';
import { useBrands } from '../api/admin';
import { apiGet, apiPost } from '../api/client';
import { cn } from '../lib/cn';
import { formatRelative } from '../lib/format';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface AskResponse {
  answer: string;
  modelId: string;
  brandSlug: string | null;
  truncated: boolean;
}

interface HistoryRow {
  id: string;
  question: string;
  /** Null on rows logged before answers were kept, or when the model failed. */
  answer: string | null;
  at: string;
  brandId: string | null;
  brandName: string | null;
  by: string;
}

/** The picker value meaning "no brandId — ask across every brand". */
const ALL_BRANDS = 'all';

/** His own examples, one click away — they teach what the page is for. */
const STARTERS = [
  'How is the agent set up to handle refund saves?',
  'What happens when a customer threatens a chargeback?',
  'When does the agent hand off to a person?',
];

export function AskPage() {
  const client = useQueryClient();
  const brands = useBrands();
  const brandRows = brands.data ?? [];
  const [brandChoice, setBrandChoice] = useState('');
  // '' means "not chosen yet" and falls to the first brand; 'all' is explicit.
  const selected = brandChoice || brandRows[0]?.id || '';
  const askingAll = selected === ALL_BRANDS;

  const [thread, setThread] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  // Set while the thread shows a logged exchange rather than a live one —
  // the caption that dates it, cleared the moment anything fresh happens.
  const [loadedNote, setLoadedNote] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const history = useQuery({
    queryKey: ['explain', 'history'],
    queryFn: () => apiGet<HistoryRow[]>('/api/explain/history'),
  });

  const ask = useMutation({
    mutationFn: (input: { brandId?: string; question: string; history: ChatTurn[] }) =>
      apiPost<AskResponse>('/api/explain', input),
    onSuccess: async (response) => {
      setThread((current) => [...current, { role: 'assistant', content: response.answer }]);
      await client.invalidateQueries({ queryKey: ['explain', 'history'] });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread, ask.isPending]);

  const send = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || !selected || ask.isPending) return;
    // The history sent is the thread BEFORE this question; the server
    // appends the question itself.
    ask.mutate({
      ...(askingAll ? {} : { brandId: selected }),
      question: trimmed,
      history: thread,
    });
    setThread((current) => [...current, { role: 'user', content: trimmed }]);
    setDraft('');
    setLoadedNote(null);
  };

  /** A logged exchange, reopened: the thread becomes that Q&A, and the brand
   *  picker follows the row so a follow-up question asks the RIGHT config. */
  const openLogged = (row: HistoryRow) => {
    if (!row.answer) {
      // Nothing stored to show (an old row, or the model failed that day) —
      // the click still helps: the question lands back in the box.
      setDraft(row.question);
      return;
    }
    setBrandChoice(row.brandId ?? ALL_BRANDS);
    setThread([
      { role: 'user', content: row.question },
      { role: 'assistant', content: row.answer },
    ]);
    setDraft('');
    setLoadedNote(
      `Logged answer from ${formatRelative(row.at)} — the setup may have changed since; re-ask to refresh.`,
    );
  };

  const brandName = askingAll
    ? 'all brands'
    : (brandRows.find((row) => row.id === selected)?.name ?? '');

  return (
    <div className="mx-auto flex h-full max-w-[1150px] gap-4 px-5 py-5">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <p className="text-xs text-ink-400">
            Answers come from the live configuration — playbooks, bot rules, the refund policy —
            and say where they came from. On &ldquo;All brands&rdquo; the answer splits by brand
            where the setup differs.
          </p>
          <div className="ml-auto flex items-center gap-2">
            {thread.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setThread([]);
                  setLoadedNote(null);
                }}
                className="flex items-center gap-1 rounded-sm px-2 py-1 text-2xs font-semibold text-ink-500 shadow-hairline hover:text-ink-900"
              >
                <Eraser size={12} strokeWidth={1.75} aria-hidden /> Clear
              </button>
            ) : null}
            <label className="flex items-center gap-1.5 text-xs text-ink-500">
              Brand
              <select
                value={selected}
                onChange={(event) => {
                  setBrandChoice(event.target.value);
                  // The old answers were about the old brand's config.
                  setThread([]);
                  setLoadedNote(null);
                }}
                className="rounded-sm bg-surface px-2 py-1 text-xs font-semibold text-ink-900 shadow-hairline"
              >
                <option value={ALL_BRANDS}>All brands</option>
                {brandRows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg bg-surface px-4 py-4 shadow-hairline">
          {loadedNote ? <p className="text-center text-2xs text-ink-400">{loadedNote}</p> : null}
          {thread.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-ink-700">
                Ask how the agent is set up{brandName ? ` for ${brandName}` : ''}.
              </p>
              <div className="mx-auto mt-4 flex max-w-md flex-col gap-2">
                {STARTERS.map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => send(starter)}
                    disabled={!selected || ask.isPending}
                    className="rounded-lg bg-surface-sunken px-3 py-2 text-left text-xs text-ink-700 shadow-hairline transition-colors duration-fast hover:text-ink-900"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            thread.map((turn, index) => (
              <div
                key={index}
                className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                    turn.role === 'user'
                      ? 'bg-accent-50 text-accent-700'
                      : 'bg-surface-sunken text-ink-900',
                  )}
                >
                  {turn.content}
                </div>
              </div>
            ))
          )}
          {ask.isPending ? <p className="text-xs text-ink-400">Reading the config…</p> : null}
          {ask.error ? (
            <p className="text-xs text-stop-700">
              {ask.error instanceof Error ? ask.error.message : 'That question failed — try again.'}
            </p>
          ) : null}
          <div ref={endRef} />
        </div>

        <form
          className="mt-3 flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            send(draft);
          }}
        >
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(draft);
              }
            }}
            rows={2}
            placeholder={brandName ? `Ask about ${brandName}…` : 'Ask about the setup…'}
            className="min-h-[44px] flex-1 resize-y rounded-lg bg-surface px-3 py-2 text-sm text-ink-900 shadow-hairline placeholder:text-ink-400 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-500"
          />
          <button
            type="submit"
            disabled={!draft.trim() || !selected || ask.isPending}
            className="flex h-[44px] items-center gap-1.5 rounded-lg bg-ink-900 px-4 text-sm font-semibold text-paper disabled:opacity-40"
          >
            Ask <CornerDownLeft size={13} strokeWidth={2} aria-hidden />
          </button>
        </form>
      </div>

      {/* The question log: who asked what, when, about which brand. A click
          reopens the logged exchange — question AND answer — and moves the
          brand picker to the row's brand, so a follow-up asks the config the
          answer was about. Rows with no stored answer (logged before answers
          were kept, or the model failed) fall back to refilling the box. */}
      <aside className="hidden w-72 shrink-0 flex-col lg:flex">
        <div className="eyebrow mb-2">Asked before</div>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg bg-surface shadow-hairline">
          {history.isLoading ? (
            <p className="px-3 py-6 text-center text-xs text-ink-400">Loading…</p>
          ) : (history.data ?? []).length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-ink-400">
              Nothing asked yet. Questions land here with who asked and when.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {(history.data ?? []).map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => openLogged(row)}
                    title={
                      row.answer
                        ? 'Show this question and its answer'
                        : 'Put this question back in the box'
                    }
                    className="block w-full px-3 py-2 text-left transition-colors duration-fast hover:bg-surface-sunken"
                  >
                    <div className="line-clamp-2 text-xs text-ink-900">{row.question}</div>
                    <div className="mt-0.5 truncate text-2xs text-ink-400">
                      {row.by} · {formatRelative(row.at)} · {row.brandName ?? 'All brands'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
